import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Home, Cat, CheckSquare, CalendarDays, Package,
  Plus, Trash2, Edit, Check, Circle, AlertTriangle,
  Phone, Heart, RefreshCw, Star, X, ChevronRight, ChevronLeft,
  ShoppingCart, StickyNote
} from 'lucide-react';
import { CompraTab } from '@/components/familia/CompraTab';
import { NotasTab } from '@/components/familia/NotasTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FRECUENCIAS = [
  { value: 'diaria', label: 'Diaria' },
  { value: 'semanal', label: 'Semanal' },
  { value: '2_semana', label: '2 veces/semana' },
  { value: '3_semana', label: '3 veces/semana' },
  { value: 'mensual', label: 'Mensual' },
];

const TIPO_EVENTO = {
  gato: { label: 'Gato', color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', icon: Cat },
  casa: { label: 'Casa', color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', icon: Home },
  humano: { label: 'Humano', color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', icon: Heart },
};

// Paleta de colores deterministas por miembro (anillo de avatar y color principal).
// Cada miembro recibe un color estable basado en su user_id.
const MEMBER_PALETTE = [
  { ring: '#fbbf24', bg: 'rgba(251,191,36,0.15)', text: '#fcd34d', name: 'ámbar' },   // amber
  { ring: '#60a5fa', bg: 'rgba(96,165,250,0.15)', text: '#93c5fd', name: 'azul' },     // blue
  { ring: '#34d399', bg: 'rgba(52,211,153,0.15)', text: '#6ee7b7', name: 'menta' },    // emerald
  { ring: '#f472b6', bg: 'rgba(244,114,182,0.15)', text: '#f9a8d4', name: 'rosa' },    // pink
  { ring: '#a78bfa', bg: 'rgba(167,139,250,0.15)', text: '#c4b5fd', name: 'lila' },    // violet
  { ring: '#fb7185', bg: 'rgba(251,113,133,0.15)', text: '#fda4af', name: 'coral' },   // rose
  { ring: '#22d3ee', bg: 'rgba(34,211,238,0.15)', text: '#67e8f9', name: 'turquesa' }, // cyan
];

const memberColor = (userId) => {
  if (!userId) return MEMBER_PALETTE[0];
  // hash sencillo deterministico
  let h = 0;
  for (let i = 0; i < String(userId).length; i++) h = (h * 31 + String(userId).charCodeAt(i)) >>> 0;
  return MEMBER_PALETTE[h % MEMBER_PALETTE.length];
};

const memberInitials = (nombre) => {
  if (!nombre) return '?';
  const parts = String(nombre).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
};

const getSaludo = () => {
  const h = new Date().getHours();
  if (h < 6) return 'Buenas noches';
  if (h < 13) return 'Buenos días';
  if (h < 21) return 'Buenas tardes';
  return 'Buenas noches';
};

const getFechaLarga = () => {
  const d = new Date();
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
};

export default function ControlFamiliar() {
  const [tab, setTab] = useState('dashboard');
  // Calendario mensual del Inicio
  const [mesCalendario, setMesCalendario] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() }; // month 0-11
  });
  const [selectedDayDate, setSelectedDayDate] = useState(null); // 'YYYY-MM-DD' or null
  // Filtro por miembro (tap en avatar de cabecera)
  const [filterMiembro, setFilterMiembro] = useState(null);
  // Form inline para añadir tarea desde el modal del día
  const [dayAddForm, setDayAddForm] = useState(null); // { nombre, asignado_a, repeticion } or null
  const [dashboard, setDashboard] = useState(null);
  const [gato, setGato] = useState({});
  const [tareas, setTareas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [loading, setLoading] = useState(true);

  // Forms
  const [showGatoForm, setShowGatoForm] = useState(false);
  const [gatoForm, setGatoForm] = useState({});
  const [showTareaForm, setShowTareaForm] = useState(false);
  const [tareaForm, setTareaForm] = useState({ nombre: '', frecuencia: 'diaria', asignado_a: '', asignado_nombre: '' });
  const [editTarea, setEditTarea] = useState(null);
  const [showEventoForm, setShowEventoForm] = useState(false);
  const [eventoForm, setEventoForm] = useState({ titulo: '', fecha: '', tipo: 'casa', notificacion_dias: 1 });
  const [editEvento, setEditEvento] = useState(null);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [calDaySelected, setCalDaySelected] = useState(null); // 'YYYY-MM-DD' o null
  const [showInvForm, setShowInvForm] = useState(false);
  const [invForm, setInvForm] = useState({ tipo: 'comida', nombre: '', cantidad: 0, unidad: 'kg', umbral: 1 });
  const [editInv, setEditInv] = useState(null);
  const [showReasignar, setShowReasignar] = useState(null);

  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadAll = useCallback(async () => {
    try {
      const [dashRes, miembrosRes] = await Promise.all([
        axios.get(`${API}/familia/dashboard`, { headers }),
        axios.get(`${API}/familia/miembros`, { headers })
      ]);
      setDashboard(dashRes.data);
      setMiembros(miembrosRes.data);
      setGato(dashRes.data.gato || {});
      setTareas(dashRes.data.tareas_hoy || []);
      setEventos(dashRes.data.proximos_eventos || []);
      setInventario(dashRes.data.inventario || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadTareas = async () => {
    const { data } = await axios.get(`${API}/familia/tareas`, { headers });
    setTareas(data);
  };
  const loadEventos = async () => {
    const { data } = await axios.get(`${API}/familia/eventos`, { headers });
    setEventos(data);
  };
  const loadInventario = async () => {
    const { data } = await axios.get(`${API}/familia/inventario`, { headers });
    setInventario(data);
  };
  const loadGato = async () => {
    const { data } = await axios.get(`${API}/familia/gato`, { headers });
    setGato(data);
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  // --- Handlers ---
  const saveGato = async () => {
    await axios.post(`${API}/familia/gato`, gatoForm, { headers });
    setShowGatoForm(false);
    loadGato();
  };

  const saveTarea = async () => {
    if (!tareaForm.nombre.trim()) return;
    if (editTarea) {
      await axios.put(`${API}/familia/tareas/${editTarea}`, tareaForm, { headers });
    } else {
      await axios.post(`${API}/familia/tareas`, tareaForm, { headers });
    }
    setShowTareaForm(false);
    setEditTarea(null);
    setTareaForm({ nombre: '', frecuencia: 'diaria', asignado_a: '', asignado_nombre: '' });
    loadTareas();
  };

  const deleteTarea = async (id) => {
    if (!window.confirm('Eliminar tarea?')) return;
    await axios.delete(`${API}/familia/tareas/${id}`, { headers });
    loadTareas();
  };

  const toggleTarea = async (id) => {
    await axios.post(`${API}/familia/tareas/${id}/completar`, {}, { headers });
    loadTareas();
  };

  const reasignarTarea = async (tareaId, miembro) => {
    await axios.put(`${API}/familia/tareas/${tareaId}`, {
      asignado_a: miembro.user_id,
      asignado_nombre: miembro.nombre
    }, { headers });
    setShowReasignar(null);
    loadTareas();
  };

  const saveEvento = async () => {
    if (!eventoForm.titulo.trim() || !eventoForm.fecha) return;
    if (editEvento) {
      await axios.put(`${API}/familia/eventos/${editEvento}`, eventoForm, { headers });
    } else {
      await axios.post(`${API}/familia/eventos`, eventoForm, { headers });
    }
    setShowEventoForm(false);
    setEditEvento(null);
    setEventoForm({ titulo: '', fecha: '', tipo: 'casa', notificacion_dias: 1 });
    loadEventos();
  };

  const deleteEvento = async (id) => {
    if (!window.confirm('Eliminar evento?')) return;
    await axios.delete(`${API}/familia/eventos/${id}`, { headers });
    loadEventos();
  };

  const saveInv = async () => {
    if (!invForm.nombre.trim()) return;
    if (editInv) {
      await axios.put(`${API}/familia/inventario/${editInv}`, invForm, { headers });
    } else {
      await axios.post(`${API}/familia/inventario`, invForm, { headers });
    }
    setShowInvForm(false);
    setEditInv(null);
    setInvForm({ tipo: 'comida', nombre: '', cantidad: 0, unidad: 'kg', umbral: 1 });
    loadInventario();
  };

  const deleteInv = async (id) => {
    if (!window.confirm('Eliminar item?')) return;
    await axios.delete(`${API}/familia/inventario/${id}`, { headers });
    loadInventario();
  };

  const updateInvCantidad = async (id, cantidad) => {
    await axios.put(`${API}/familia/inventario/${id}`, { cantidad: parseFloat(cantidad) }, { headers });
    loadInventario();
  };

  const hoy = new Date().toISOString().slice(0, 10);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-dark-mk flex items-center justify-center">
          <div className="text-gray-500">Cargando...</div>
        </div>
      </>
    );
  }

  // --- TABS ---
  const tabs = [
    { id: 'dashboard', label: 'Inicio', icon: Home },
    { id: 'tareas', label: 'Tareas', icon: CheckSquare },
    { id: 'calendario', label: 'Eventos', icon: CalendarDays },
    { id: 'compra', label: 'Compra', icon: ShoppingCart },
    { id: 'notas', label: 'Notas', icon: StickyNote },
    { id: 'inventario', label: 'Stock', icon: Package },
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-dark-mk" data-testid="control-familiar">

        {/* Header — saludo dinámico cálido */}
        <div className="px-4 pt-5 pb-4" data-testid="familia-header">
          <div className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-600/20 via-amber-500/10 to-orange-500/5 p-4">
            {/* Glow decorativo */}
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-amber-400/10 blur-2xl pointer-events-none" />
            <div className="relative flex items-center gap-3">
              <div className="text-3xl select-none" aria-hidden="true">🏠</div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-300/80 text-[10px] uppercase tracking-[0.18em] font-bold">Centro de Control Familiar</p>
                <h1 className="text-xl font-bold text-white leading-tight">{getSaludo()}, familia</h1>
                <p className="text-amber-200/70 text-xs capitalize mt-0.5">{getFechaLarga()}</p>
              </div>
              {/* Avatares grupales (clickables → filtran por miembro) */}
              {miembros.length > 0 && (
                <div className="flex -space-x-2 flex-shrink-0">
                  {miembros.slice(0, 4).map(m => {
                    const c = memberColor(m.user_id);
                    const isFiltered = filterMiembro === m.user_id;
                    return (
                      <button key={m.user_id}
                        onClick={() => setFilterMiembro(isFiltered ? null : m.user_id)}
                        className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-[11px] font-bold ring-2 transition-all ${isFiltered ? 'scale-110 ring-white' : 'ring-dark-mk hover:scale-105'}`}
                        style={{ borderColor: c.ring, backgroundColor: c.bg, color: c.text }}
                        title={isFiltered ? `Mostrar todos` : `Solo ${m.nombre}`}
                        data-testid={`member-avatar-${m.user_id}`}
                      >
                        {memberInitials(m.nombre)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="px-4 mb-4">
          <div className="flex bg-gray-900/80 rounded-lg p-0.5 border border-gray-800" data-testid="familia-tabs">
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-md text-[10px] font-medium transition-all ${
                    tab === t.id ? 'bg-amber-500/20 text-amber-400' : 'text-gray-500 hover:text-gray-300'
                  }`} data-testid={`tab-${t.id}`}>
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ═══ DASHBOARD TAB ═══ */}
        {tab === 'dashboard' && dashboard && (() => {
          // Construir grid del mes (lunes inicio)
          const year = mesCalendario.year;
          const month = mesCalendario.month;
          const firstDay = new Date(year, month, 1);
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          // weekday: 0=domingo, 1=lunes, ... 6=sábado. Queremos lunes como 0.
          const startWeekday = (firstDay.getDay() + 6) % 7;
          const cells = [];
          for (let i = 0; i < startWeekday; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            cells.push({ day: d, dateStr, weekday: new Date(year, month, d).getDay() });
          }
          while (cells.length % 7 !== 0) cells.push(null);

          // Mapa abreviado de día de la semana en formato corto (lun..dom)
          const DOW_KEYS = ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'];
          const taskAppliesToDay = (t, cell) => {
            if (filterMiembro && t.asignado_a !== filterMiembro) return false;
            // 1) Tareas únicas con fecha específica
            if (t.fecha_especifica) return t.fecha_especifica === cell.dateStr;
            // 2) Tareas con días de la semana definidos (independiente de la frecuencia)
            if (Array.isArray(t.dias_semana) && t.dias_semana.length > 0) {
              return t.dias_semana.includes(DOW_KEYS[cell.weekday]);
            }
            // 3) Lógica legacy por frecuencia
            const isToday = cell.dateStr === hoy;
            if (t.frecuencia === 'diaria') return true;
            if (t.frecuencia === 'mensual') return cell.day === 1;
            return isToday; // semanal sin días específicos
          };
          // Helper: items para un día determinado
          const itemsForDay = (cell) => {
            if (!cell) return { eventos: [], tareas: [], miembros: new Set() };
            const dayEventos = eventos.filter(ev => ev.fecha === cell.dateStr);
            const dayTareas = tareas.filter(t => taskAppliesToDay(t, cell));
            const eventosFiltered = filterMiembro
              ? dayEventos.filter(ev => !ev.miembros || ev.miembros.length === 0 || ev.miembros.includes(filterMiembro))
              : dayEventos;
            const miembrosSet = new Set();
            dayTareas.forEach(t => t.asignado_a && miembrosSet.add(t.asignado_a));
            return { eventos: eventosFiltered, tareas: dayTareas, miembros: miembrosSet };
          };

          const monthLabel = new Date(year, month, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
          const goPrev = () => setMesCalendario(m => {
            const nm = m.month === 0 ? 11 : m.month - 1;
            const ny = m.month === 0 ? m.year - 1 : m.year;
            return { year: ny, month: nm };
          });
          const goNext = () => setMesCalendario(m => {
            const nm = m.month === 11 ? 0 : m.month + 1;
            const ny = m.month === 11 ? m.year + 1 : m.year;
            return { year: ny, month: nm };
          });
          const goToday = () => {
            const d = new Date();
            setMesCalendario({ year: d.getFullYear(), month: d.getMonth() });
          };

          return (
          <div className="px-4 pb-24 space-y-4" data-testid="dashboard-tab">

            {filterMiembro && (
              <div className="flex items-center justify-between bg-gray-900/60 border border-gray-700 rounded-xl px-3 py-2" data-testid="filter-banner">
                {(() => {
                  const m = miembros.find(x => x.user_id === filterMiembro);
                  const c = memberColor(filterMiembro);
                  return (
                    <>
                      <span className="text-xs flex items-center gap-2" style={{ color: c.text }}>
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.ring }} />
                        Mostrando solo {m?.nombre || 'miembro'}
                      </span>
                      <button onClick={() => setFilterMiembro(null)} className="text-xs text-gray-400 hover:text-white">Ver todos</button>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Calendario mensual */}
            <div className="bg-gray-900/80 border border-gray-800 rounded-2xl p-3" data-testid="dash-calendario">
              <div className="flex items-center justify-between mb-3 px-1">
                <button onClick={goPrev} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white" data-testid="cal-prev">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2">
                  <h3 className="text-white text-sm font-bold capitalize">{monthLabel}</h3>
                  <button onClick={goToday} className="text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300 border border-amber-500/30 rounded-md px-2 py-0.5" data-testid="cal-today">Hoy</button>
                </div>
                <button onClick={goNext} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white" data-testid="cal-next">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Cabecera días */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d, i) => (
                  <div key={i} className="text-center text-[10px] font-bold text-gray-500 uppercase">{d}</div>
                ))}
              </div>

              {/* Grid */}
              <div className="grid grid-cols-7 gap-1">
                {cells.map((cell, idx) => {
                  if (!cell) return <div key={idx} className="min-h-[72px]" />;
                  const isToday = cell.dateStr === hoy;
                  const isWeekend = cell.weekday === 0 || cell.weekday === 6;
                  const { eventos: dayEv, tareas: dayTa } = itemsForDay(cell);
                  const hasItems = dayEv.length + dayTa.length > 0;
                  // Combinar tareas + eventos en una sola lista visual ordenada
                  const items = [
                    ...dayEv.map(ev => ({ kind: 'evento', label: ev.titulo, color: '#60a5fa' })),
                    ...dayTa.map(t => {
                      const c = t.asignado_a ? memberColor(t.asignado_a) : { ring: '#9ca3af', bg: 'rgba(156,163,175,0.15)', text: '#d1d5db' };
                      return { kind: 'tarea', label: t.nombre, color: c.ring, bg: c.bg, text: c.text, tarea: t };
                    }),
                  ];
                  const maxVisible = 3;
                  const visible = items.slice(0, maxVisible);
                  const overflow = Math.max(0, items.length - maxVisible);
                  return (
                    <button key={idx}
                      onClick={() => setSelectedDayDate(cell.dateStr)}
                      className={`min-h-[72px] rounded-lg p-1 flex flex-col items-stretch gap-0.5 transition-all border cursor-pointer text-left ${
                        isToday
                          ? 'border-amber-400 bg-amber-500/15'
                          : hasItems
                            ? 'border-gray-700 bg-gray-800/60 hover:border-amber-500/40'
                            : 'border-gray-800/40 bg-transparent hover:border-gray-700 hover:bg-gray-800/30'
                      }`}
                      data-testid={`cal-day-${cell.dateStr}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-[11px] font-bold leading-none ${
                          isToday ? 'text-amber-300' : isWeekend ? 'text-gray-500' : 'text-gray-300'
                        }`}>{cell.day}</span>
                        {dayEv.length > 0 && (
                          <span className="text-[8px] text-blue-300 font-bold leading-none">📅</span>
                        )}
                      </div>
                      <div className="flex flex-col gap-[2px] mt-0.5 overflow-hidden">
                        {visible.map((it, i) => {
                          const isEvento = it.kind === 'evento';
                          const done = !isEvento && it.tarea && (it.tarea.completado_hoy || it.tarea.ultimo_completado === hoy) && isToday;
                          return (
                            <span key={i}
                              className={`text-[8px] leading-tight font-semibold rounded-sm px-1 py-[1px] truncate ${done ? 'line-through opacity-60' : ''}`}
                              style={{
                                backgroundColor: isEvento ? 'rgba(96,165,250,0.18)' : it.bg,
                                color: isEvento ? '#93c5fd' : it.text,
                                borderLeft: `2px solid ${it.color}`
                              }}
                              title={it.label}>
                              {it.label}
                            </span>
                          );
                        })}
                        {overflow > 0 && (
                          <span className="text-[8px] text-gray-500 px-1 leading-tight">+{overflow}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Leyenda mini */}
              <div className="flex items-center gap-3 justify-center mt-3 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Hoy</span>
                <span className="flex items-center gap-1">📅 Eventos</span>
                <span className="flex items-center gap-1">Tap día para detalle/+ tarea</span>
              </div>
            </div>

            {/* Tareas pendientes hoy (resumen rápido) */}
            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4" data-testid="dash-tareas">
              <div className="flex items-center gap-2 mb-3">
                <CheckSquare className="h-4 w-4 text-green-400" />
                <span className="text-green-400 text-xs font-bold uppercase tracking-wider">Tareas hoy</span>
                {(() => {
                  const visibles = tareas.filter(t => !filterMiembro || t.asignado_a === filterMiembro);
                  return <span className="ml-auto text-gray-600 text-xs">{visibles.filter(t => t.completado_hoy || t.ultimo_completado === hoy).length}/{visibles.length}</span>;
                })()}
              </div>
              {tareas.filter(t => !filterMiembro || t.asignado_a === filterMiembro).length === 0 ? (
                <p className="text-gray-600 text-sm">{filterMiembro ? 'Sin tareas para este miembro' : 'No hay tareas creadas'}</p>
              ) : (
                <div className="space-y-2">
                  {tareas.filter(t => !filterMiembro || t.asignado_a === filterMiembro).map(t => {
                    const done = t.completado_hoy || t.ultimo_completado === hoy;
                    const c = t.asignado_a ? memberColor(t.asignado_a) : null;
                    return (
                      <button key={t.id} onClick={() => toggleTarea(t.id)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-left ${
                          done ? 'bg-green-900/20 border border-green-500/30' : 'bg-gray-800/60 border border-gray-700/60 hover:border-gray-600'
                        }`} data-testid={`dash-tarea-${t.id}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          done ? 'bg-green-500 text-white' : 'bg-gray-700 border border-gray-600'
                        }`}>
                          {done ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3 text-gray-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${done ? 'text-green-300/80 line-through' : 'text-white'}`}>{t.nombre}</p>
                          {t.asignado_nombre ? (
                            <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: c?.text || '#9ca3af' }}>
                              <span className="w-3.5 h-3.5 rounded-full border flex items-center justify-center text-[7px] font-bold"
                                style={{ borderColor: c?.ring, backgroundColor: c?.bg, color: c?.text }}>
                                {memberInitials(t.asignado_nombre)}
                              </span>
                              {t.asignado_nombre}
                            </span>
                          ) : (
                            <p className="text-gray-500 text-[10px] italic">Sin asignar</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Proximos eventos */}
            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4" data-testid="dash-eventos">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-blue-400" />
                <span className="text-blue-400 text-xs font-bold uppercase tracking-wider">Proximos eventos</span>
              </div>
              {eventos.length === 0 ? (
                <p className="text-gray-600 text-sm">No hay eventos proximos</p>
              ) : (
                <div className="space-y-2">
                  {eventos.slice(0, 5).map(ev => {
                    const tc = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.casa;
                    const Icon = tc.icon;
                    const daysUntil = Math.ceil((new Date(ev.fecha + 'T00:00:00') - new Date(hoy + 'T00:00:00')) / (1000*60*60*24));
                    return (
                      <div key={ev.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${tc.bg} border ${tc.border}`}>
                        <Icon className={`h-4 w-4 ${tc.color} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{ev.titulo}</p>
                          <p className="text-gray-500 text-[10px]">{ev.fecha}</p>
                        </div>
                        <span className={`text-xs font-bold tabular-nums ${daysUntil <= 3 ? 'text-red-400' : tc.color}`}>
                          {daysUntil === 0 ? 'HOY' : daysUntil === 1 ? 'Manana' : `${daysUntil}d`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {/* Modal del día seleccionado */}
        {selectedDayDate && (() => {
          const DOW_KEYS = ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'];
          const DOW_LABELS = { dom: 'Domingos', lun: 'Lunes', mar: 'Martes', mie: 'Miércoles', jue: 'Jueves', vie: 'Viernes', sab: 'Sábados' };
          const isToday = selectedDayDate === hoy;
          const cellDate = new Date(selectedDayDate + 'T00:00:00');
          const cellDay = cellDate.getDate();
          const cellWeekday = cellDate.getDay();
          const cellDowKey = DOW_KEYS[cellWeekday];

          const taskApplies = (t) => {
            if (filterMiembro && t.asignado_a !== filterMiembro) return false;
            if (t.fecha_especifica) return t.fecha_especifica === selectedDayDate;
            if (Array.isArray(t.dias_semana) && t.dias_semana.length > 0) {
              return t.dias_semana.includes(cellDowKey);
            }
            if (t.frecuencia === 'diaria') return true;
            if (t.frecuencia === 'mensual') return cellDay === 1;
            return isToday;
          };

          const dayEv = eventos.filter(ev => ev.fecha === selectedDayDate);
          const dayTa = tareas.filter(taskApplies);
          const grupos = {};
          dayTa.forEach(t => {
            const k = t.asignado_a || 'sin_asignar';
            if (!grupos[k]) grupos[k] = { miembro: t.asignado_nombre || 'Sin asignar', user_id: t.asignado_a, items: [] };
            grupos[k].items.push(t);
          });

          const closeModal = () => { setSelectedDayDate(null); setDayAddForm(null); };
          const openAddForm = () => setDayAddForm({
            nombre: '', asignado_a: miembros[0]?.user_id || '', repeticion: 'una_vez'
          });
          const saveTask = async () => {
            if (!dayAddForm?.nombre.trim() || !dayAddForm.asignado_a) {
              toast.error('Pon nombre y asignado'); return;
            }
            const m = miembros.find(x => x.user_id === dayAddForm.asignado_a);
            const payload = {
              nombre: dayAddForm.nombre.trim(),
              asignado_a: dayAddForm.asignado_a,
              asignado_nombre: m?.nombre || '',
            };
            if (dayAddForm.repeticion === 'una_vez') {
              payload.frecuencia = 'unica';
              payload.fecha_especifica = selectedDayDate;
            } else if (dayAddForm.repeticion === 'cada_semana') {
              payload.frecuencia = 'semanal';
              payload.dias_semana = [cellDowKey];
            } else if (dayAddForm.repeticion === 'diaria') {
              payload.frecuencia = 'diaria';
            } else if (dayAddForm.repeticion === 'mensual') {
              payload.frecuencia = 'mensual';
            }
            try {
              await axios.post(`${API}/familia/tareas`, payload, { headers });
              toast.success('Tarea añadida');
              setDayAddForm(null);
              await loadAll();
            } catch (e) { toast.error('Error al guardar'); }
          };

          return (
          <Dialog open onOpenChange={(o) => !o && closeModal()}>
            <DialogContent className="bg-gray-900 border-gray-800 max-w-md" data-testid="day-modal">
              <DialogHeader>
                <DialogTitle className="text-white capitalize flex items-center justify-between">
                  <span>{cellDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {dayEv.length === 0 && dayTa.length === 0 && !dayAddForm && (
                  <p className="text-gray-500 text-sm text-center py-4">Sin tareas ni eventos este día.</p>
                )}
                {dayEv.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-1.5">Eventos</p>
                    <div className="space-y-1.5">
                      {dayEv.map(ev => {
                        const tc = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.casa;
                        const Icon = tc.icon;
                        return (
                          <div key={ev.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${tc.bg} border ${tc.border}`}>
                            <Icon className={`h-4 w-4 ${tc.color} flex-shrink-0`} />
                            <p className="text-white text-sm font-medium flex-1 truncate">{ev.titulo}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {Object.values(grupos).map(g => {
                  const c = g.user_id ? memberColor(g.user_id) : { ring: '#6b7280', bg: 'rgba(75,85,99,0.15)', text: '#9ca3af' };
                  return (
                    <div key={g.user_id || 'na'}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-[9px] font-bold"
                          style={{ borderColor: c.ring, backgroundColor: c.bg, color: c.text }}>
                          {memberInitials(g.miembro)}
                        </span>
                        <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: c.text }}>{g.miembro} · {g.items.length}</p>
                      </div>
                      <div className="space-y-1.5 pl-2">
                        {g.items.map(t => {
                          const done = isToday && (t.completado_hoy || t.ultimo_completado === hoy);
                          const repTag = t.fecha_especifica ? 'Único'
                            : (Array.isArray(t.dias_semana) && t.dias_semana.length > 0)
                              ? t.dias_semana.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join('·')
                              : (FRECUENCIAS.find(f => f.value === t.frecuencia)?.label || t.frecuencia);
                          return (
                            <button key={t.id}
                              onClick={() => isToday && toggleTarea(t.id)}
                              disabled={!isToday}
                              className={`w-full flex items-center gap-2.5 p-2 rounded-lg text-left transition-all ${
                                done ? 'bg-green-900/20 border border-green-500/30' :
                                isToday ? 'bg-gray-800/60 border border-gray-700/60 hover:border-gray-500 cursor-pointer' :
                                          'bg-gray-800/40 border border-gray-800 opacity-70'
                              }`}
                              data-testid={`modal-tarea-${t.id}`}>
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                done ? 'bg-green-500 text-white' : 'bg-gray-700 border border-gray-600'
                              }`}>
                                {done ? <Check className="h-3 w-3" /> : <Circle className="h-2.5 w-2.5 text-gray-600" />}
                              </div>
                              <span className={`text-sm flex-1 truncate ${done ? 'text-green-300/80 line-through' : 'text-white'}`}>{t.nombre}</span>
                              <span className="text-[9px] text-gray-500 uppercase">{repTag}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {!isToday && dayTa.length > 0 && (
                  <p className="text-[10px] text-amber-400/70 italic text-center pt-2 border-t border-gray-800">
                    Las tareas solo se pueden marcar el mismo día
                  </p>
                )}

                {/* Form inline para añadir tarea a este día */}
                {dayAddForm ? (
                  <div className="bg-gray-800/80 border border-amber-500/30 rounded-xl p-3 space-y-2" data-testid="add-task-form">
                    <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Nueva tarea</p>
                    <Input value={dayAddForm.nombre}
                      onChange={e => setDayAddForm(p => ({ ...p, nombre: e.target.value }))}
                      placeholder="Ej: Sacar la basura"
                      className="bg-gray-900 border-gray-700 text-white text-sm h-9"
                      autoFocus
                      data-testid="input-task-nombre" />
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase mb-1">Asignar a</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {miembros.map(m => {
                          const c = memberColor(m.user_id);
                          const sel = dayAddForm.asignado_a === m.user_id;
                          return (
                            <button key={m.user_id}
                              onClick={() => setDayAddForm(p => ({ ...p, asignado_a: m.user_id }))}
                              className="text-xs px-2 py-1 rounded-lg flex items-center gap-1.5 transition-all"
                              style={sel
                                ? { backgroundColor: c.bg, color: c.text, border: `1px solid ${c.ring}` }
                                : { backgroundColor: 'rgba(31,41,55,0.6)', color: '#9ca3af', border: '1px solid rgba(55,65,81,0.6)' }}
                              data-testid={`pick-member-${m.user_id}`}>
                              <span className="w-4 h-4 rounded-full border flex items-center justify-center text-[8px] font-bold"
                                style={{ borderColor: c.ring, backgroundColor: sel ? c.ring : 'transparent', color: sel ? '#0b0d13' : c.text }}>
                                {memberInitials(m.nombre)}
                              </span>
                              {m.nombre}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase mb-1">Repetición</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { val: 'una_vez', label: 'Solo este día' },
                          { val: 'cada_semana', label: `Cada ${DOW_LABELS[cellDowKey].toLowerCase()}` },
                          { val: 'diaria', label: 'Todos los días' },
                          { val: 'mensual', label: 'Mensual (día 1)' },
                        ].map(opt => (
                          <button key={opt.val}
                            onClick={() => setDayAddForm(p => ({ ...p, repeticion: opt.val }))}
                            className={`text-[11px] px-2 py-1.5 rounded-lg text-left transition-colors ${
                              dayAddForm.repeticion === opt.val
                                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                : 'bg-gray-900/60 text-gray-400 border border-gray-700 hover:text-white'
                            }`}
                            data-testid={`pick-rep-${opt.val}`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button onClick={saveTask}
                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-gray-900 h-8 text-xs font-bold"
                        data-testid="btn-save-task">
                        <Check className="h-3.5 w-3.5 mr-1" /> Guardar
                      </Button>
                      <Button onClick={() => setDayAddForm(null)} variant="ghost"
                        className="text-gray-400 hover:text-white h-8 text-xs">Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <Button onClick={openAddForm}
                    className="w-full bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 h-9 text-xs font-bold"
                    data-testid="btn-open-add-task">
                    <Plus className="h-4 w-4 mr-1" /> Añadir tarea a este día
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
          );
        })()}

        {/* ═══ TAREAS TAB ═══ */}
        {tab === 'tareas' && (
          <div className="px-4 pb-24" data-testid="tareas-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">{tareas.length} tareas</p>
              <Button size="sm" onClick={() => { setTareaForm({ nombre: '', frecuencia: 'diaria', asignado_a: '', asignado_nombre: '' }); setEditTarea(null); setShowTareaForm(true); }}
                className="bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 h-8 text-xs"
                data-testid="btn-add-tarea">
                <Plus className="h-3.5 w-3.5 mr-1" /> Nueva
              </Button>
            </div>
            <div className="space-y-2">
              {tareas.map(t => {
                const done = t.completado_hoy || t.ultimo_completado === hoy;
                return (
                  <div key={t.id} className={`rounded-xl p-3 border transition-all ${
                    done ? 'bg-green-900/20 border-green-500/30' : 'bg-gray-900/80 border-gray-800'
                  }`} data-testid={`tarea-${t.id}`}>
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggleTarea(t.id)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          done ? 'bg-green-500 text-white' : 'bg-gray-800 border border-gray-600 hover:border-gray-400'
                        }`} data-testid={`check-tarea-${t.id}`}>
                        {done ? <Check className="h-4 w-4" /> : <Circle className="h-3 w-3 text-gray-600" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${done ? 'text-green-300/80 line-through' : 'text-white'}`}>{t.nombre}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-gray-500 text-[10px]">{FRECUENCIAS.find(f => f.value === t.frecuencia)?.label || t.frecuencia}</span>
                          {t.asignado_a ? (
                            (() => {
                              const c = memberColor(t.asignado_a);
                              return (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium" style={{ color: c.text }}>
                                  <span className="w-4 h-4 rounded-full border flex items-center justify-center text-[8px] font-bold"
                                    style={{ borderColor: c.ring, backgroundColor: c.bg, color: c.text }}>
                                    {memberInitials(t.asignado_nombre)}
                                  </span>
                                  {t.asignado_nombre}
                                </span>
                              );
                            })()
                          ) : (
                            <span className="text-gray-600 text-[10px] italic">Sin asignar</span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => setShowReasignar(showReasignar === t.id ? null : t.id)}
                        className="text-gray-600 hover:text-amber-400 p-1.5" title="Reasignar" data-testid={`btn-reasignar-${t.id}`}>
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => { setTareaForm({ nombre: t.nombre, frecuencia: t.frecuencia, asignado_a: t.asignado_a, asignado_nombre: t.asignado_nombre }); setEditTarea(t.id); setShowTareaForm(true); }}
                        className="text-gray-600 hover:text-white p-1.5" data-testid={`btn-edit-tarea-${t.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => deleteTarea(t.id)}
                        className="text-gray-600 hover:text-red-400 p-1.5" data-testid={`btn-del-tarea-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {/* Reasignar dropdown */}
                    {showReasignar === t.id && (
                      <div className="mt-2 ml-10 flex gap-2 flex-wrap" data-testid={`reasignar-${t.id}`}>
                        {miembros.map(m => {
                          const c = memberColor(m.user_id);
                          const active = t.asignado_a === m.user_id;
                          return (
                            <button key={m.user_id} onClick={() => reasignarTarea(t.id, m)}
                              className="text-xs px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5"
                              style={active
                                ? { backgroundColor: c.bg, color: c.text, border: `1px solid ${c.ring}` }
                                : { backgroundColor: 'rgba(31,41,55,0.6)', color: '#9ca3af', border: '1px solid rgba(55,65,81,0.6)' }}
                            >
                              <span className="w-4 h-4 rounded-full border flex items-center justify-center text-[8px] font-bold"
                                style={{ borderColor: c.ring, backgroundColor: active ? c.ring : 'transparent', color: active ? '#0b0d13' : c.text }}>
                                {memberInitials(m.nombre)}
                              </span>
                              {m.nombre}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ CALENDARIO/EVENTOS TAB ═══ */}
        {tab === 'calendario' && (
          <div className="px-4 pb-24" data-testid="eventos-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">{eventos.length} eventos</p>
              <Button size="sm" onClick={() => { setEventoForm({ titulo: '', fecha: calDaySelected || '', tipo: 'casa', notificacion_dias: 1 }); setEditEvento(null); setShowEventoForm(true); }}
                className="bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 h-8 text-xs"
                data-testid="btn-add-evento">
                <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo
              </Button>
            </div>

            {/* === Calendario mensual === */}
            {(() => {
              const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
              const daysShort = ['L','M','X','J','V','S','D'];
              const { y, m } = calMonth;
              const firstDay = new Date(y, m, 1);
              const startWeekday = (firstDay.getDay() + 6) % 7; // L=0 ... D=6
              const daysInMonth = new Date(y, m + 1, 0).getDate();
              const eventsByDay = {};
              eventos.forEach(ev => {
                const [yy, mm] = (ev.fecha || '').split('-').map(Number);
                if (yy === y && (mm - 1) === m) {
                  const d = parseInt(ev.fecha.split('-')[2], 10);
                  if (!eventsByDay[d]) eventsByDay[d] = [];
                  eventsByDay[d].push(ev);
                }
              });
              const todayStr = hoy;
              const todayY = parseInt(todayStr.slice(0, 4), 10);
              const todayM = parseInt(todayStr.slice(5, 7), 10) - 1;
              const todayD = parseInt(todayStr.slice(8, 10), 10);
              const goPrev = () => setCalMonth(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 });
              const goNext = () => setCalMonth(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 });
              const cells = [];
              for (let i = 0; i < startWeekday; i++) cells.push(null);
              for (let d = 1; d <= daysInMonth; d++) cells.push(d);
              return (
                <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-3 mb-4" data-testid="eventos-calendar">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <button onClick={goPrev} className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800" data-testid="cal-prev">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-white font-bold text-sm capitalize">{monthNames[m]} {y}</span>
                    <button onClick={goNext} className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800" data-testid="cal-next">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {daysShort.map(d => (
                      <div key={d} className="text-[10px] text-gray-500 text-center font-bold">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {cells.map((d, idx) => {
                      if (d === null) return <div key={`e-${idx}`} />;
                      const evs = eventsByDay[d] || [];
                      const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      const isToday = todayY === y && todayM === m && todayD === d;
                      const isSelected = calDaySelected === dateStr;
                      const types = [...new Set(evs.map(e => e.tipo))];
                      return (
                        <button
                          key={d}
                          onClick={() => setCalDaySelected(isSelected ? null : dateStr)}
                          className={`relative aspect-square rounded-lg flex flex-col items-center justify-center transition-all
                            ${isSelected ? 'bg-blue-500/40 border border-blue-400' :
                              isToday ? 'bg-amber-500/15 border border-amber-500/40' :
                              evs.length > 0 ? 'bg-gray-800 border border-gray-700 hover:bg-gray-700' :
                              'bg-gray-900/40 hover:bg-gray-800 border border-transparent'}`}
                          data-testid={`cal-day-${dateStr}`}
                        >
                          <span className={`text-xs font-bold ${isToday ? 'text-amber-400' : isSelected ? 'text-white' : evs.length > 0 ? 'text-white' : 'text-gray-500'}`}>{d}</span>
                          {types.length > 0 && (
                            <div className="flex gap-0.5 mt-0.5">
                              {types.slice(0, 3).map(tp => {
                                const tc = TIPO_EVENTO[tp] || TIPO_EVENTO.casa;
                                const dotColor = tp === 'gato' ? 'bg-amber-400' : tp === 'casa' ? 'bg-blue-400' : 'bg-green-400';
                                return <span key={tp} className={`w-1.5 h-1.5 rounded-full ${dotColor}`} title={tc.label} />;
                              })}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* Leyenda */}
                  <div className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-gray-800 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Gato</span>
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Casa</span>
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Humano</span>
                  </div>
                </div>
              );
            })()}

            {/* === Eventos del día / próximos === */}
            {(() => {
              const listToShow = calDaySelected
                ? eventos.filter(ev => ev.fecha === calDaySelected)
                : eventos;
              if (calDaySelected) {
                return (
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-blue-300 text-xs font-bold">Eventos del {calDaySelected}</p>
                    <button onClick={() => setCalDaySelected(null)} className="text-gray-500 hover:text-white text-[11px] underline">Ver todos</button>
                  </div>
                );
              }
              return null;
            })()}

            <div className="space-y-2">
              {(calDaySelected ? eventos.filter(ev => ev.fecha === calDaySelected) : eventos).map(ev => {
                const tc = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.casa;
                const Icon = tc.icon;
                const daysUntil = Math.ceil((new Date(ev.fecha + 'T00:00:00') - new Date(hoy + 'T00:00:00')) / (1000*60*60*24));
                return (
                  <div key={ev.id} className={`rounded-xl p-3 border ${tc.bg} ${tc.border}`} data-testid={`evento-${ev.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-800/80`}>
                        <Icon className={`h-4 w-4 ${tc.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{ev.titulo}</p>
                        <p className="text-gray-500 text-[10px]">{ev.fecha} &middot; {tc.label}</p>
                      </div>
                      <span className={`text-xs font-bold tabular-nums flex-shrink-0 ${daysUntil <= 3 ? 'text-red-400' : tc.color}`}>
                        {daysUntil === 0 ? 'HOY' : daysUntil < 0 ? 'Pasado' : `${daysUntil}d`}
                      </span>
                      <button onClick={() => { setEventoForm({...ev}); setEditEvento(ev.id); setShowEventoForm(true); }}
                        className="text-gray-600 hover:text-white p-1" data-testid={`btn-edit-ev-${ev.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => deleteEvento(ev.id)}
                        className="text-gray-600 hover:text-red-400 p-1" data-testid={`btn-del-ev-${ev.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {(calDaySelected ? eventos.filter(ev => ev.fecha === calDaySelected) : eventos).length === 0 && (
                <div className="text-center py-12 text-gray-600">
                  <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{calDaySelected ? 'Sin eventos este día' : 'No hay eventos próximos'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ INVENTARIO TAB ═══ */}
        {/* ═══ COMPRA TAB ═══ */}
        {tab === 'compra' && <CompraTab headers={headers} />}

        {/* ═══ NOTAS TAB ═══ */}
        {tab === 'notas' && <NotasTab headers={headers} />}

        {/* ═══ INVENTARIO TAB ═══ */}
        {tab === 'inventario' && (
          <div className="px-4 pb-24" data-testid="inventario-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Stock del gato</p>
              <Button size="sm" onClick={() => { setInvForm({ tipo: 'comida', nombre: '', cantidad: 0, unidad: 'kg', umbral: 1 }); setEditInv(null); setShowInvForm(true); }}
                className="bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 h-8 text-xs"
                data-testid="btn-add-inv">
                <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo
              </Button>
            </div>
            <div className="space-y-3">
              {inventario.map(inv => {
                const isLow = inv.cantidad <= inv.umbral;
                return (
                  <div key={inv.id} className={`rounded-xl p-4 border ${isLow ? 'bg-red-900/15 border-red-500/30' : 'bg-gray-900/80 border-gray-800'}`}
                    data-testid={`inv-${inv.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Package className={`h-4 w-4 ${isLow ? 'text-red-400' : 'text-purple-400'}`} />
                        <span className="text-white font-bold text-sm">{inv.nombre}</span>
                        <span className="text-gray-600 text-[10px] uppercase">{inv.tipo}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setInvForm({...inv}); setEditInv(inv.id); setShowInvForm(true); }}
                          className="text-gray-600 hover:text-white p-1"><Edit className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteInv(inv.id)}
                          className="text-gray-600 hover:text-red-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    {/* Quantity controls */}
                    <div className="flex items-center gap-3">
                      <button onClick={() => updateInvCantidad(inv.id, Math.max(0, inv.cantidad - 0.5))}
                        className="w-8 h-8 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white text-lg font-bold">-</button>
                      <div className="flex-1 text-center">
                        <span className={`text-3xl font-black tabular-nums ${isLow ? 'text-red-400' : 'text-white'}`}>{inv.cantidad}</span>
                        <span className="text-gray-500 text-sm ml-1">{inv.unidad}</span>
                      </div>
                      <button onClick={() => updateInvCantidad(inv.id, inv.cantidad + 0.5)}
                        className="w-8 h-8 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white text-lg font-bold">+</button>
                    </div>
                    {/* Alert bar */}
                    <div className="mt-2">
                      <div className="w-full bg-gray-800 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full transition-all ${
                          isLow ? 'bg-red-500' : inv.cantidad <= inv.umbral * 2 ? 'bg-amber-500' : 'bg-green-500'
                        }`} style={{ width: `${Math.min(100, (inv.cantidad / (inv.umbral * 3)) * 100)}%` }} />
                      </div>
                      <p className="text-gray-600 text-[9px] mt-1">Umbral alerta: {inv.umbral} {inv.unidad}</p>
                    </div>
                    {isLow && (
                      <div className="mt-2 flex items-center gap-2 bg-red-500/10 rounded-lg px-3 py-1.5 border border-red-500/20">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                        <span className="text-red-400 text-xs font-bold">Stock bajo - reponer</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {inventario.length === 0 && (
                <div className="text-center py-12 text-gray-600">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Añade items de inventario del gato</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ DIALOGS ═══ */}

        {/* Gato form */}
        <Dialog open={showGatoForm} onOpenChange={setShowGatoForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>Perfil del gato</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Nombre *</label>
                <Input value={gatoForm.nombre || ''} onChange={e => setGatoForm(p => ({...p, nombre: e.target.value}))}
                  className="bg-gray-800 border-gray-700 text-white" data-testid="input-gato-nombre" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Fecha nacimiento</label>
                <Input type="date" value={gatoForm.fecha_nacimiento || ''} onChange={e => setGatoForm(p => ({...p, fecha_nacimiento: e.target.value}))}
                  className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Veterinario</label>
                  <Input value={gatoForm.veterinario_nombre || ''} onChange={e => setGatoForm(p => ({...p, veterinario_nombre: e.target.value}))}
                    placeholder="Nombre" className="bg-gray-800 border-gray-700 text-white text-sm" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Telefono vet.</label>
                  <Input type="tel" value={gatoForm.veterinario_telefono || ''} onChange={e => setGatoForm(p => ({...p, veterinario_telefono: e.target.value}))}
                    placeholder="Telefono" className="bg-gray-800 border-gray-700 text-white text-sm" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Microchip</label>
                <Input value={gatoForm.microchip || ''} onChange={e => setGatoForm(p => ({...p, microchip: e.target.value}))}
                  placeholder="Opcional" className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Observaciones</label>
                <textarea value={gatoForm.observaciones || ''} onChange={e => setGatoForm(p => ({...p, observaciones: e.target.value}))}
                  placeholder="Alergias, medicacion, etc."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-20 focus:outline-none focus:border-amber-500/50" />
              </div>
              <Button onClick={saveGato} className="w-full bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 font-bold"
                data-testid="btn-save-gato">
                Guardar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Tarea form */}
        <Dialog open={showTareaForm} onOpenChange={setShowTareaForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>{editTarea ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Tarea *</label>
                <Input value={tareaForm.nombre} onChange={e => setTareaForm(p => ({...p, nombre: e.target.value}))}
                  placeholder="Ej: Limpiar arenero" className="bg-gray-800 border-gray-700 text-white" data-testid="input-tarea-nombre" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Frecuencia</label>
                <select value={tareaForm.frecuencia} onChange={e => setTareaForm(p => ({...p, frecuencia: e.target.value}))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                  {FRECUENCIAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Asignar a</label>
                <div className="flex gap-2">
                  {miembros.map(m => (
                    <button key={m.user_id} onClick={() => setTareaForm(p => ({...p, asignado_a: m.user_id, asignado_nombre: m.nombre}))}
                      className={`flex-1 text-xs py-2 rounded-lg transition-all ${
                        tareaForm.asignado_a === m.user_id
                          ? 'bg-amber-500/30 text-amber-400 border border-amber-500/40'
                          : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                      }`} data-testid={`assign-${m.nombre}`}>
                      {m.nombre}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={saveTarea} disabled={!tareaForm.nombre.trim()}
                className="w-full bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 font-bold"
                data-testid="btn-save-tarea">
                {editTarea ? 'Actualizar' : 'Crear tarea'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Evento form */}
        <Dialog open={showEventoForm} onOpenChange={setShowEventoForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>{editEvento ? 'Editar evento' : 'Nuevo evento'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Titulo *</label>
                <Input value={eventoForm.titulo} onChange={e => setEventoForm(p => ({...p, titulo: e.target.value}))}
                  placeholder="Ej: Vacuna anual" className="bg-gray-800 border-gray-700 text-white" data-testid="input-evento-titulo" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Fecha *</label>
                <Input type="date" value={eventoForm.fecha} onChange={e => setEventoForm(p => ({...p, fecha: e.target.value}))}
                  className="bg-gray-800 border-gray-700 text-white" data-testid="input-evento-fecha" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Tipo</label>
                <div className="flex gap-2">
                  {Object.entries(TIPO_EVENTO).map(([key, tc]) => {
                    const Icon = tc.icon;
                    return (
                      <button key={key} onClick={() => setEventoForm(p => ({...p, tipo: key}))}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs transition-all ${
                          eventoForm.tipo === key ? `${tc.bg} ${tc.color} border ${tc.border}` : 'bg-gray-800 text-gray-500 border border-gray-700'
                        }`} data-testid={`tipo-${key}`}>
                        <Icon className="h-3.5 w-3.5" /> {tc.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Avisar con antelacion</label>
                <select value={eventoForm.notificacion_dias} onChange={e => setEventoForm(p => ({...p, notificacion_dias: parseInt(e.target.value)}))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                  <option value={0}>Mismo dia</option>
                  <option value={1}>1 dia antes</option>
                  <option value={2}>2 dias antes</option>
                  <option value={3}>3 dias antes</option>
                  <option value={7}>1 semana antes</option>
                  <option value={14}>2 semanas antes</option>
                </select>
              </div>
              <Button onClick={saveEvento} disabled={!eventoForm.titulo.trim() || !eventoForm.fecha}
                className="w-full bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 font-bold"
                data-testid="btn-save-evento">
                {editEvento ? 'Actualizar' : 'Crear evento'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Inventario form */}
        <Dialog open={showInvForm} onOpenChange={setShowInvForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>{editInv ? 'Editar item' : 'Nuevo item'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Nombre *</label>
                <Input value={invForm.nombre} onChange={e => setInvForm(p => ({...p, nombre: e.target.value}))}
                  placeholder="Ej: Pienso Royal Canin" className="bg-gray-800 border-gray-700 text-white" data-testid="input-inv-nombre" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Tipo</label>
                  <select value={invForm.tipo} onChange={e => setInvForm(p => ({...p, tipo: e.target.value}))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                    <option value="comida">Comida</option>
                    <option value="arena">Arena</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Unidad</label>
                  <select value={invForm.unidad} onChange={e => setInvForm(p => ({...p, unidad: e.target.value}))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                    <option value="kg">kg</option>
                    <option value="sacos">sacos</option>
                    <option value="unidades">unidades</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Cantidad actual</label>
                  <Input type="number" step="0.5" min="0" value={invForm.cantidad}
                    onChange={e => setInvForm(p => ({...p, cantidad: parseFloat(e.target.value) || 0}))}
                    className="bg-gray-800 border-gray-700 text-white" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Umbral alerta</label>
                  <Input type="number" step="0.5" min="0" value={invForm.umbral}
                    onChange={e => setInvForm(p => ({...p, umbral: parseFloat(e.target.value) || 0}))}
                    className="bg-gray-800 border-gray-700 text-white" />
                </div>
              </div>
              <Button onClick={saveInv} disabled={!invForm.nombre.trim()}
                className="w-full bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 font-bold"
                data-testid="btn-save-inv">
                {editInv ? 'Actualizar' : 'Crear item'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </>
  );
}
